import {
  CardFeedResponse,
  CardFeedStatus,
  CardNetwork,
} from 'src/queries/cardFeeds/cardFeeds.models'

export type CardFeedSortField =
  | 'cardProgramName'
  | 'referenceId'
  | 'cardNetwork'
  | 'dateConnected'
  | 'cardCount'
  | 'status'

/**
 * Drives all sort/filter/search for the card-feeds landing table. Field names
 * mirror the eventual server request shape (search / statuses / sortField /
 * sortDirection) so a future move to server-side filtering is a localized swap.
 * Mirrors src/app/CardFeedDetails/utils/selectVisibleCards.ts.
 */
export interface CardFeedsView {
  search?: string
  statuses?: CardFeedStatus[]
  sortField?: CardFeedSortField
  sortDirection?: 'ASC' | 'DESC'
}

// Logical ordering for the Status column — attention-first, not alphabetical.
const STATUS_RANK: Record<CardFeedStatus, number> = {
  SUBMITTED: 0,
  ACTIVE: 1,
  DEACTIVATED: 2,
  FAILED: 3,
}

// Brand label used for search + network sort so users match what they see.
// Keep in sync with NETWORK_LABEL in cardFeedsColumns.tsx.
const NETWORK_LABEL: Record<CardNetwork, string> = {
  VISA: 'Visa',
  MASTERCARD: 'Mastercard',
  AMERICAN_EXPRESS: 'American Express',
}

const networkLabel = (network: CardNetwork): string =>
  NETWORK_LABEL[network] ?? network

const matchesSearch = (feed: CardFeedResponse, term: string): boolean => {
  const haystack = [
    feed.cardProgramName,
    feed.referenceId ?? '',
    networkLabel(feed.cardNetwork),
  ]
    .join(' ')
    .toLowerCase()
  return haystack.includes(term)
}

const compareByField = (
  a: CardFeedResponse,
  b: CardFeedResponse,
  field: CardFeedSortField,
): number => {
  switch (field) {
    case 'dateConnected':
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    case 'status':
      return STATUS_RANK[a.status] - STATUS_RANK[b.status]
    case 'cardCount': {
      // Missing count sorts last in ascending order.
      const av = a.cardCount ?? Number.POSITIVE_INFINITY
      const bv = b.cardCount ?? Number.POSITIVE_INFINITY
      return av - bv
    }
    case 'cardNetwork':
      return networkLabel(a.cardNetwork)
        .toLowerCase()
        .localeCompare(networkLabel(b.cardNetwork).toLowerCase())
    case 'referenceId':
      return (a.referenceId ?? '')
        .toLowerCase()
        .localeCompare((b.referenceId ?? '').toLowerCase())
    case 'cardProgramName':
    default:
      return (a.cardProgramName || '')
        .toLowerCase()
        .localeCompare((b.cardProgramName || '').toLowerCase())
  }
}

export const selectVisibleFeeds = (
  feeds: CardFeedResponse[],
  view: CardFeedsView,
): CardFeedResponse[] => {
  let result = feeds

  const term = view.search?.trim().toLowerCase()
  if (term) {
    result = result.filter((feed) => matchesSearch(feed, term))
  }

  if (view.statuses && view.statuses.length > 0) {
    const allowed = new Set(view.statuses)
    result = result.filter((feed) => allowed.has(feed.status))
  }

  if (view.sortField) {
    const direction = view.sortDirection === 'DESC' ? -1 : 1
    // Copy before sort — never mutate the caller's array (React Query cache).
    result = [...result].sort(
      (a, b) =>
        compareByField(a, b, view.sortField as CardFeedSortField) * direction,
    )
  }

  return result
}
